/// <amd-module name="Test/TestResultDetails/Components/TestResultDetailsView" />
import * as React from "react";
import {
    ViewContext,
    IViewContextData
} from "Test/common-util/Common";
import {
    IResultDetailsPaneTabContributionContext,
    IResultsToolbarContributionContext,
} from "Test/common-util/Contracts/ContributedExtension.Contracts";
import { IContributedToolbarProps, ITestResultTreeData } from "Test/common-util/Contracts/ViewContracts";
import * as Resources from "Test/common-util/Resources";
import * as TcmPerfScenarios from "Test/common-util/TCMPerfScenarios";
import { TestResultDetailsActionCreator } from "Test/TestResultDetails/Actions/TestResultDetailsActionCreator";
import { TestResultDetailsActionHub } from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { DebugDetailsComponent } from "Test/TestResultDetails/Components/DebugDetails";
import { ResultDetailsHeaderComponent } from "Test/TestResultDetails/Components/ResultDetailsHeader";
import {
    ITestResultDetailsViewState,
    TestResultDetailsViewStore,
} from "Test/TestResultDetails/Store/TestResultDetailsViewStore";
import { ContributedPivotBarItemProvider } from "VSS/Features/Frame/ContributedPivotBarItemProvider";
import { IVssContributableActionService } from "VSS/Features/Frame/IVssContributableActionService";
import { IContributedCommand, IContributedService, IVssContributionService } from "VSS/Platform/Contribution";
import {
    Components,
    IVssComponentContext,
    IVssComponentProperties,
    VssComponent,
    WrappedComponent,
} from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssContextualMenuItem } from "VSSUI/Components/VssContextualMenu/VssContextualMenu.Props";
import { Icon } from "VSSUI/Icon";
import { IPivotBarAction, IVssPivotBarItemProvider, PivotBar, PivotBarItem } from "VSSUI/PivotBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { TestResultDetailsPanePivot, TestMode } from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { IPageStateHistoryService, HistoryBehavior } from "VSS/Features/History";

const LoadingComponent = () => (
    <Spinner
        size={SpinnerSize.large}
    />
);

export interface ITestResultDetailsViewProps extends IVssComponentProperties {
    testResultTreeData?: ITestResultTreeData;
    viewContext: IViewContextData;
    isFullScreen: boolean;
    showHistory: boolean;
    showHeader: boolean;
    hideTestCaseTitle?: boolean;
    showBugAndLink?: boolean;
    linkedStackTrace?: boolean;
    runId?: number;
    resultId?: number;
    paneView?: string;
    isNoConfigRuns?: boolean;
    onPivotChanged?: (pivotKey: string) => void;
    onError?: (error: any) => void;
}

module AttachmentSource {
    export const testRun: string = "Test Run";
    export const testResult: string = "Test Result";
}

export class TestResultDetailsView extends VssComponent<ITestResultDetailsViewProps, ITestResultDetailsViewState, IVssComponentContext> {

    constructor(props: ITestResultDetailsViewProps, context: IVssComponentContext) {
        super(props, context);

        this._testResultDetailsActionsHub = new TestResultDetailsActionHub();
        this._testResultDetailsActionCreator = new TestResultDetailsActionCreator(this._testResultDetailsActionsHub,
            context.pageContext,
            this.props.viewContext);
        this._store = new TestResultDetailsViewStore("TestResultDetails",
            this._testResultDetailsActionsHub,
            context.pageContext,
            this.props.viewContext);
        this.state = this._store.getState();
        this._pageStateHistoryService = context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this._performanceService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public componentDidUpdate(prevProps: ITestResultDetailsViewProps): void {
        if (this._checkIfPropsChanged(prevProps)) {
            this._initializeData();
        }
        if (!this.state.isLoading) {
            this._performanceService.endScenario(ViewContext[this.props.viewContext.viewContext], TcmPerfScenarios.TestResultDetailsViewLoaded, false);
        }
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._handleStoreChange);
        this._initializeData();
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._handleStoreChange);
        this._store.dispose();
    }

    public render(): JSX.Element {
        let element: JSX.Element = <div></div>;
        if (this.state.isLoading) {
            return <Spinner
                size={SpinnerSize.large}
                className="flex-column test-result-details-view"
            />
        }

        if (this.state.loadingErrorMessage) {
            return <MessageBar
                severity={MessageBarSeverity.Error}>
                {this.state.loadingErrorMessage}
            </MessageBar>
        }

        if (this.state.testMode === TestMode.TestResult) {
            element = this._getTestResultdetailsViewBody();
        } else {
            element = this._getTestRunDetailsViewBody();

        }
        return element;
    }

    private _getPivotProviders(): IVssPivotBarItemProvider[] | undefined {
        try {
            const loadingComponent = () => (
                <Spinner className="about-loading-spinner" />
            );
            return ([
                new ContributedPivotBarItemProvider({
                    pageContext: this.context.pageContext,
                    getContext: this._getExtensionProps,
                    tabGroupIds: ["ms.vss-test-web.test-result-details-tab-items"],
                    loadingComponent: loadingComponent
                })
            ]);
        }
        catch (error) { }
    }

    private _getExtensionProps = (contributionId: string): IResultDetailsPaneTabContributionContext => {
        const runId: number = (this.props.testResultTreeData && this.props.testResultTreeData.runId) ? this.props.testResultTreeData.runId : this.props.runId as number;
        const resultId: number = (this.props.testResultTreeData && this.props.testResultTreeData.resultId) ? this.props.testResultTreeData.resultId : this.props.resultId as number;
        const subResultId: number = (this.props.testResultTreeData && this.props.testResultTreeData.subResultId) ? this.props.testResultTreeData.subResultId as number : 0;

        return {
            runId: runId,
            resultId: resultId,
            subResultId: subResultId,
            viewContext: this.props.viewContext
        };
    }

    private _initializeData(): void {
        // this condition should first check treedata because in case we have both tree data and runId/resultId preference need to be given to treedata.
        if (this.props.testResultTreeData) {
            let testresultDetailsPromise: Promise<void> = this._testResultDetailsActionCreator.initialize(this.props.testResultTreeData, this.props.paneView);
            testresultDetailsPromise.then(() => {
            }, (error) => {
                if (this.props.onError) {
                    this.props.onError(error);
                }
                else {
                    this._testResultDetailsActionsHub.onError.invoke(error);
                }
            });
        } else if (this.props.runId) {
            this._testResultDetailsActionCreator.initializeWithFullScreen(this.props.runId, this.props.resultId ? this.props.resultId : 0, this.props.paneView);
        }
    }

    private _checkIfPropsChanged(prevProps: ITestResultDetailsViewProps): boolean {
        if (this.props.runId !== prevProps.runId || this.props.resultId !== prevProps.resultId) {
            return true;
        }

        return this._compareTestResultTreeData(this.props.testResultTreeData, prevProps.testResultTreeData);
    }

    private _compareTestResultTreeData(currentTreeData?: ITestResultTreeData, prevTreeData?: ITestResultTreeData): boolean {
        if (currentTreeData && prevTreeData) {

            if (currentTreeData.runId !== prevTreeData.runId) {
                return true;
            }

            if (currentTreeData.resultId !== prevTreeData.resultId) {
                return true;
            }

            if (currentTreeData.subResultId !== prevTreeData.subResultId) {
                return true;
            }
        } else if (currentTreeData) {
            return true;
        }
        return false;
    }

    private _handleStoreChange = (): void => {

        this.setState(this._store.getState());

    }

    private _getTestResultdetailsViewBody(): JSX.Element {
        let shouldShowTestRunName: boolean = this.props.isFullScreen && !!this.state.testResults
            && !!this.state.testResults.result && !!this.state.testResults.result.testRun;

        return (<div className="flex-column test-result-details-view">
            {
                this.state.testResults &&
                this.props.showHeader &&
                !this.props.hideTestCaseTitle &&
                <div className="test-case-title flex-row">
                    {
                        shouldShowTestRunName &&
                        <span className="test-run-name">
                            {this.state.testResults.result.testRun.name}
                        </span>
                    }
                    {
                        shouldShowTestRunName &&
                        <Icon className="test-title-sep" iconName="ChevronRight"></Icon>
                    }
                    <Tooltip overflowOnly={true} text={this.state.testResults.test}>
                        <span className="test-case-name">
                            {this.state.testResults.test}
                        </span>
                    </Tooltip>
                </div>
            }
            {
                this.state.testResults &&
                this.props.showHeader &&
                <div className="test-result-header">
                    <ResultDetailsHeaderComponent
                        outcome={this.state.testResults.outcome}
                        duration={this.state.testResults.duration}
                        state={this.state.testResults.state}
                        startedDate={this.state.testResults.startedDate}
                        completedDate={this.state.testResults.completedDate}
                        failingContextName={this.state.testResults.failingContextName}
                        failingContextId={this.state.testResults.failingContextId}
                        isCurrentArtifact={this.state.testResults.isCurrentArtifact}
                        owner={this.state.testResults.owner}
                        computerName={this.state.testResults.computerName}
                        viewContext={this.props.viewContext.viewContext}
                        testMode={TestMode.TestResult}
                        detailsPaneMode={this.props.isFullScreen ? "FullScreen" : "HalfScreen"}
                        failingSince={this.state.testResults.failingSince}
                        currentHeadeState={this.state.currentHeaderState}
                        testResultDetailsActionCreator={this._testResultDetailsActionCreator}
                    />
                </div>
            }
            {
                !!this.state.testResults &&
                <PivotBar
                    selectedPivot={this.state.selectedPivot}
                    className="flex-grow scroll-auto test-result-details-pivot-bar"
                    onPivotClicked={this._onPivotClicked}
                    pivotProviders={this._getPivotProviders()}
                    commands={
                        ((this.props.isFullScreen || this.props.showBugAndLink) && this.state.testMode === TestMode.TestResult) && !this.props.isNoConfigRuns
                            ? this._getPivotBarActions()
                            : undefined
                    }
                >
                    <PivotBarItem
                        itemKey={TestResultDetailsPanePivot.Debug}
                        name={Resources.DebugPivotLabel}
                        order={1}
                        className="customPadding test-details-pane-debug test-result-details-pivot-bar-customPadding">
                        <DebugDetailsComponent
                            comment={this.state.testResults.comment}
                            errorMessage={this.state.testResults.errorMessage}
                            stackTrace={this.state.testResults.stackTrace}
                            linkedStackTrace={this.props.linkedStackTrace}
                            viewContext={this.props.viewContext}
                            isFullScreen={this.props.isFullScreen}>
                        </DebugDetailsComponent>
                    </PivotBarItem>

                    <PivotBarItem
                        itemKey={TestResultDetailsPanePivot.WorkItems}
                        name={Resources.WorkItemsText}
                        order={2}
                        className="customPadding test-details-pane-workItems test-result-details-pivot-bar-customPadding">
                        <WrappedComponent
                            wrappedType="test-resultWorkItems"
                            dependencies={["ms.vss-test-web.test-resultworkitems"]}
                            viewContext={this.props.viewContext}
                            testResults={this.state.testResults}
                            testCaseResult={this.state.testResults.result}
                            loadingComponent={LoadingComponent}
                        />
                    </PivotBarItem>

                    <PivotBarItem
                        className="customPadding test-details-pane-attachments"
                        itemKey={TestResultDetailsPanePivot.Attachments}
                        order={3}
                        name={Resources.AttachmentsHeaderText}>
                        <WrappedComponent
                            wrappedType="test-attachmentsview"
                            dependencies={["ms.vss-test-web.attachments-view"]}
                            testRunId={this.state.testResults && this.state.testResults.runId ? this.state.testResults.runId : 0}
                            testResultId={this.state.testResults && this.state.testResults.result && this.state.testResults.result && this.state.testResults.result.id ? this.state.testResults.result.id : 0}
                            subResultId={this.state.testResults && this.state.testResults.subResultId ? this.state.testResults.subResultId : 0}
                            viewContext={this.props.viewContext}
                            loadingComponent={LoadingComponent}
                        />
                    </PivotBarItem>

                    <PivotBarItem
                        className="customPadding test-result-details-pivot-bar-customPadding"
                        itemKey={TestResultDetailsPanePivot.History}
                        name={Resources.HistoryHeaderText}
                        order={4}
                        hidden={!this.props.showHistory}>
                        <WrappedComponent
                            dependencies={["ms.vss-test-web.test-resulthistory"]}
                            wrappedType="test-resulthistory"
                            testCaseResult={this.state.testResults && this.state.testResults.result}
                            subResultId={this.state.testResults && this.state.testResults.subResultId ? this.state.testResults.subResultId : 0}
                            viewContext={this.props.viewContext}
                            isFullScreen={this.props.isFullScreen}
                            loadingComponent={LoadingComponent}
                        />
                    </PivotBarItem>
                </PivotBar>
            }
        </div>);
    }

    private _onPivotClicked = (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, pivotKey: string): void => {
        this._testResultDetailsActionCreator.pivotSelected(pivotKey);
        if (this.props.onPivotChanged) {
            this.props.onPivotChanged(pivotKey);
            if (this._pageStateHistoryService) {
                this._pageStateHistoryService.queueValueChange('paneView', pivotKey != undefined ? `${pivotKey}` : "", HistoryBehavior.replace);
            }
        }
    }

    private _getTestRunDetailsViewBody(): JSX.Element {
        return (
            <div className="flex-column test-result-details-view">
                {
                    this._getTestRunNameElement()
                }
                {
                    this._getTestRunHeaderElement()
                }
                {
                    this._getRunPivotBarElement()
                }
            </div>
        );
    }

    private _getTestRunNameElement(): JSX.Element {
        return (
            <div className="test-case-title">
                {
                    this.state.testRun &&
                    <Tooltip overflowOnly={true} text={this.state.testRun.name}>
                        <span className="test-case-name">
                            {this.state.testRun.name}
                        </span>
                    </Tooltip>
                }
            </div>
        );
    }

    private _getTestRunHeaderElement(): JSX.Element {
        return (
            <div className="test-result-header">
                {
                    this.state.testRun &&
                    <ResultDetailsHeaderComponent
                        duration={this.state.testRun.duration}
                        state={this.state.testRun.state}
                        startedDate={this.state.testRun.startedDate}
                        completedDate={this.state.testRun.completedDate}
                        testMode={TestMode.TestRun}
                        tags={this.state.testRun.tags}
                    />
                }
            </div>

        );
    }

    private _getRunPivotBarElement(): JSX.Element {
        return (
            <PivotBar
                selectedPivot={this.state.selectedPivot}
                onPivotClicked={this._onPivotClicked}
                pivotProviders={this._getPivotProviders()}
                className="test-result-details-pivot-bar"
            >
                <PivotBarItem
                    itemKey={TestResultDetailsPanePivot.Debug}
                    name={Resources.DebugPivotLabel}
                    order={1}
                    className="customPadding test-details-pane-debug test-result-details-pivot-bar-customPadding">
                    {
                        this.state.testRun &&
                        <DebugDetailsComponent
                            errorMessage={this.state.testRun.errorMessage}
                            comment={this.state.testRun.comment}
                            isFullScreen={this.props.isFullScreen}>
                        </DebugDetailsComponent>
                    }
                </PivotBarItem>
                <PivotBarItem
                    className="customPadding test-details-pane-attachments"
                    itemKey={TestResultDetailsPanePivot.Attachments}
                    order={2}
                    name={Resources.AttachmentsHeaderText}>
                    <WrappedComponent
                        wrappedType="test-attachmentsview"
                        dependencies={["ms.vss-test-web.attachments-view"]}
                        testRunId={this.state.testRun ? this.state.testRun.runId : 0}
                        testResultId={0}
                        subResultId={0}
                        viewContext={this.props.viewContext}
                        loadingComponent={LoadingComponent}
                    />
                </PivotBarItem>
            </PivotBar>
        );
    }

    private _getPivotBarActions(): IPivotBarAction[] {
        let pivotBarActions: IPivotBarAction[] = [];

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const children = contributionService.getContributionChildren("ms.vss-test-web.test-results-actions-menu", "ms.vss-web.action");

        if (children) {
            for (const childId of children) {
                // look up the contribution for the child
                let menuContribution: IContributedToolbarProps | undefined = contributionService.getContribution<IContributedToolbarProps>(childId);

                if (menuContribution) {
                    pivotBarActions.push(this._getPivotBarAction(menuContribution, childId, contributionService));
                }
            }
        }

        return pivotBarActions;
    }

    private _getPivotBarAction(menuContribution: IContributedToolbarProps, menuItemId: string, contributionService: IVssContributionService): IPivotBarAction {
        let commandBarItem: IPivotBarAction = {
            key: menuContribution.key ? menuContribution.key : menuItemId,
            name: menuContribution.text,
            title: menuContribution.text,
            important: true,
            iconProps: menuContribution.iconProps,
            onClick: (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item: IVssContextualMenuItem): void => {
                if (menuContribution.command) {
                    contributionService.executeCommandEx(item.key, menuContribution.command as IContributedCommand, item.key, this._getSelectedTestResults(), this.props.viewContext);
                } else {
                    this._executeThirdPartyExtension(menuContribution, item, contributionService);
                }
            }
        } as IPivotBarAction;

        if (menuContribution.subMenu) {
            commandBarItem.children = this._getPivotBarSubActions(menuContribution, contributionService);
        }

        return commandBarItem;
    }

    private _executeThirdPartyExtension = (menuContribution: IContributedToolbarProps, item: IVssContextualMenuItem, contributionService: IVssContributionService): void => {
        // Sample 3rd party extensions for Tests tab: https://github.com/paraggangil/azure-devops-extensions
        const contributedService: IContributedService = {
            serviceName: menuContribution.registeredObjectId || menuContribution.key,
            uri: menuContribution.uri,
            configuration: this.props.viewContext
        };

        contributionService.getServiceEx<IVssContributableActionService>(item.key, contributedService, true)
            .then(contributedActionService => {
                if (contributedActionService && contributedActionService.execute) {
                    contributedActionService.execute(this._getContextForExtension());
                }
            });
    }

    private _getContextForExtension(): IResultsToolbarContributionContext {
        const resultsToolbarContributionContext: IResultsToolbarContributionContext = {
            viewContext: this.props.viewContext,
            selectedTestResults: []
        };
        const selectedTestResults: ITestResultTreeData[] = [this.props.testResultTreeData] as ITestResultTreeData[];
        selectedTestResults.forEach((testResult: ITestResultTreeData) => {
            resultsToolbarContributionContext.selectedTestResults.push({
                resultId: testResult.resultId as number,
                runId: testResult.runId as number
            });
        });

        return resultsToolbarContributionContext;
    }

    private _getPivotBarSubActions(menuContribution: IContributedToolbarProps, contributionService: IVssContributionService): IPivotBarAction[] {
        let pivotBarActionSubItems: IPivotBarAction[] = [];

        if (menuContribution.subMenu) {
            for (const subMenuItem of menuContribution.subMenu) {
                pivotBarActionSubItems.push({
                    key: subMenuItem.key ? subMenuItem.key : subMenuItem.text,
                    name: subMenuItem.text,
                    iconProps: subMenuItem.iconProps,
                    onClick: menuContribution.subMenuCommand ? (ev: React.MouseEvent<HTMLElement>, item: IVssContextualMenuItem) => {
                        contributionService.executeCommandEx(item.key, menuContribution.subMenuCommand as IContributedCommand, item.key, this._getSelectedTestResults(), this.props.viewContext);
                    } : undefined
                });
            }
        }

        return pivotBarActionSubItems;
    }

    private _getSelectedTestResults(): ITestResultTreeData[] {
        let testResultDetailsViewState: ITestResultDetailsViewState = this.state || this._store.getState();
        if (testResultDetailsViewState != null && testResultDetailsViewState.testResults != null) {
            let selectedTreeData: ITestResultTreeData = {
                test: testResultDetailsViewState.testResults.test,
                runId: testResultDetailsViewState.testResults.runId,
                resultId: testResultDetailsViewState.testResults.result.id,
                testCaseRefId: testResultDetailsViewState.testResults.result.testCaseReferenceId,
                testTitle: testResultDetailsViewState.testResults.result.automatedTestName,
                storage: testResultDetailsViewState.testResults.result.automatedTestStorage
            } as ITestResultTreeData;
            return [selectedTreeData];
        }
        else if (this.props.testResultTreeData != null) {
            return [this.props.testResultTreeData];
        }
        else
            return [];
    }

    private _store: TestResultDetailsViewStore;
    private _performanceService: IVssPerformanceService;
    private _testResultDetailsActionCreator: TestResultDetailsActionCreator;
    private _testResultDetailsActionsHub: TestResultDetailsActionHub;
    private _pageStateHistoryService: IPageStateHistoryService;
}

Components.add("test-result-details", TestResultDetailsView);